import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import Select, { components as rsComponents } from 'react-select';
import { APIProvider, Map, AdvancedMarker, useMapsLibrary, useMap } from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { AREA_OPTIONS as areaOptions } from '../../../../constants/areas';
import { formatAreaOption } from '../../../../constants/areaOption';
import { colorOf as colorForArea } from '../../../../constants/areas';

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID'; // required for Advanced Markers
const START_POS = { lat: 25.2048, lng: 55.2708 }; // Dubai

// Area options (react-select)

// Helpers to avoid showing a pin when values are '', null, undefined, or 0/0
const isValidCoord = (n) => Number.isFinite(n) && Math.abs(n) > 0.000001;
const parseLatLngFromProperty = (p) => {
  const lat = Number(p?.latitude);
  const lng = Number(p?.longitude);
  return isValidCoord(lat) && isValidCoord(lng) ? { lat, lng } : null;
};

export default function PropertyDetailsSection({
  // ADD only: fill the area in from the pin instead of just suggesting it.
  autoSelectArea = false,
  newProperty = {},
  setNewProperty = () => { },
}) {
  const [position, setPosition] = useState(() => parseLatLngFromProperty(newProperty));
  const [copied, setCopied] = useState(false);
  const [isEditingLocation, setIsEditingLocation] = useState(() => !parseLatLngFromProperty(newProperty));



  // Build Google Maps link (prefer Place ID, else lat/lng)
  const buildMapsLink = useCallback((lat, lng, mapsUri = null) => {
    if (mapsUri) return mapsUri; // Prefer official Place URI (e.g., googleMapsLinks.placeURI)
    return Number.isFinite(lat) && Number.isFinite(lng)
      ? `https://maps.google.com/?q=${lat},${lng}` // coords fallback
      : '';
  }, []);

  // When a place is chosen (from PlacesSearch), we get lat/lng and placeId
  const handlePlacePick = useCallback(
    ({ lat, lng, placeId, placeDesc, mapsUri }) => {
      setPosition({ lat, lng });
      const link = buildMapsLink(lat, lng, mapsUri);
      setNewProperty((prev) => ({
        ...prev,
        latitude: Number(lat),
        longitude: Number(lng),
        google_place_id: placeId || null,
        google_place_desc: placeDesc || prev.google_place_desc || '',
        google_maps_link: link,
      }));
    },
    [buildMapsLink, setNewProperty]
  );

  // When user clicks the map, we store coords and clear place_id
  const handleMapPick = useCallback(
    ({ lat, lng }) => {
      setPosition({ lat, lng });
      const link = buildMapsLink(lat, lng);
      setNewProperty((prev) => ({
        ...prev,
        latitude: Number(lat),
        longitude: Number(lng),
        google_place_id: null,
        google_maps_link: link,
      }));
    },
    [buildMapsLink, setNewProperty]
  );

  // Copy button
  const handleCopy = useCallback(async () => {
    const link = newProperty?.google_maps_link;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  }, [newProperty?.google_maps_link]);


  const { latitude, longitude } = newProperty;

  useEffect(() => {
    // keep the map pin in sync with backend
    const parsed = parseLatLngFromProperty({ latitude, longitude });
    setPosition(parsed);

    const hasCoords =
      isValidCoord(Number(latitude)) && isValidCoord(Number(longitude));
    setIsEditingLocation(!hasCoords);
  }, [latitude, longitude]);

  // Controlled "Area" value for react-select
  const areaValue =
    areaOptions.find((opt) => opt.value === (newProperty.area || '')) || null;

  // ── AREAS ON THE MAP, AND A SUGGESTION FROM THE PIN (2026-08-30) ──────────
  // The Area dropdown and the map used to know nothing about each other, which
  // is how twenty-one Business Bay towers ended up filed as Downtown Dubai.
  // The zones are drawn so it is obvious which is which, and the pin proposes
  // the area it actually falls in.
  const [zones, setZones] = React.useState([]);
  const [suggestion, setSuggestion] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    axios.get('/api/areas/zones')
      .then((r) => { if (alive) setZones(r.data?.zones || []); })
      .catch(() => { /* the map still works without the overlays */ });
    return () => { alive = false; };
  }, []);

  // Working out the area is a round trip; say so in the field rather than
  // leaving it looking empty and idle while it happens.
  const [suggesting, setSuggesting] = React.useState(false);

  React.useEffect(() => {
    const lat = position?.lat;
    const lng = position?.lng;
    if (lat == null || lng == null) { setSuggestion(null); setSuggesting(false); return undefined; }
    let alive = true;
    setSuggesting(true);
    axios.get('/api/areas/suggest', { params: { lat, lng } })
      .then((r) => {
        if (!alive) return;
        const data = r.data || null;
        setSuggestion(data);
        // ADD ONLY. A new property has no area yet, so the pin choosing one
        // saves a guess — and the guess is what filed twenty-one Business Bay
        // towers under Downtown. On EDIT the field is left alone: changing an
        // existing property's area is a decision, not a side effect of opening
        // the form.
        if (autoSelectArea && data?.suggestion) {
          setNewProperty((prev) => (prev.area ? prev : { ...prev, area: data.suggestion }));
        }
      })
      .catch(() => { if (alive) setSuggestion(null); })
      .finally(() => { if (alive) setSuggesting(false); });
    return () => { alive = false; };
  }, [position?.lat, position?.lng, autoSelectArea, setNewProperty]);

  const suggestedArea = suggestion?.suggestion || null;
  const suggestionMatches = !!suggestedArea && suggestedArea === (newProperty.area || '');

  return (
    <APIProvider apiKey={GMAPS_KEY} libraries={['places']}>
      <div style={{ display: 'grid', gap: 12 }}>
        {/* Address details (editable) */}
        <div className="form-section">
          <h3>Address Details</h3>

          <div className="form-row">
            {/* Building Name (editable) */}
            <div className="form-group">
              <label>Building Name</label>
              <input
                type="text"
                value={newProperty.address_line_1 || ''}
                placeholder="Building name"
                onChange={(e) =>
                  setNewProperty((prev) => ({
                    ...prev,
                    address_line_1: e.target.value,
                  }))
                }
              />
            </div>

            {/* Unit Number (editable) */}
            <div className="form-group">
              <label>Unit Number</label>
              <input
                type="text"
                value={newProperty.unit_number || ''}
                placeholder="Unit number"
                onChange={(e) =>
                  setNewProperty((prev) => ({
                    ...prev,
                    unit_number: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          {/* Places search input (web component) */}
          <div className="form-group" style={{ width: '100%' }}>
            <label style={{ display: 'block', marginBottom: 6 }}>
              Search & Pin
            </label>

            {!isEditingLocation && position ? (
              // Locked view: show current pinned info + "Change" button
              <div
                className="pinned-notice"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: '#fafafa'
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Already pinned for this property</div>
                  <div
                    style={{
                      fontSize: 13,
                      color: '#4b5563',
                      marginTop: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%'
                    }}
                    title={
                      newProperty.google_place_desc ||
                      (Number.isFinite(newProperty.latitude) && Number.isFinite(newProperty.longitude)
                        ? `${newProperty.latitude}, ${newProperty.longitude}` : '')
                    }
                  >
                    {newProperty.google_place_desc
                      ? newProperty.google_place_desc
                      : (Number.isFinite(newProperty.latitude) && Number.isFinite(newProperty.longitude)
                        ? `${Number(newProperty.latitude).toFixed(6)}, ${Number(newProperty.longitude).toFixed(6)}`
                        : '')}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsEditingLocation(true)}
                  style={{
                    height: 34,
                    padding: '0 12px',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    cursor: 'pointer',
                    fontWeight: 600,
                    color: '#262626ff',
                    fontSize: 12
                  }}
                  aria-label="Change pinned address"
                  title="Change pinned address"
                >
                  Change
                </button>
              </div>
            ) : (
              // Editing view: show the actual Places field
              <div className="google-maps-places-search-bar">
                <PlacesSearch onPick={handlePlacePick} />
              </div>
            )}
          </div>

          {/* Map + a single Advanced Marker */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: 320,
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #eee',
            }}
          >
            <Map
              mapId={MAP_ID}
              defaultCenter={START_POS} // always start at Dubai (no pin)
              defaultZoom={position ? 16 : 12} // zoom in only if we have coords
              disableDefaultUI={false}
              streetViewControl={false}
              mapTypeControl={true}
              gestureHandling="greedy"
            >
              {/* The areas we cover, in the scheduling board's colours. Drawn
                  under everything else and non-clickable, so picking a location
                  by tapping the map still works exactly as before. */}
              <AreaZones zones={zones} />

              {/* only pan and show a pin when coords exist */}
              {position && <PanTo position={position} zoom={16} />}
              {position && <AdvancedMarker position={position} />}

              {/* click-to-set still works; once set, pin appears */}
              <MapClick
                onPick={handleMapPick}
                enabled={isEditingLocation}
                onRequestEnable={() => setIsEditingLocation(true)}
              />

            </Map>

            {!isEditingLocation && position && (
              <button
                type="button"
                onClick={() => setIsEditingLocation(true)}   // tap map to unlock
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.35)',
                  backdropFilter: 'blur(1px)',
                  pointerEvents: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#374151',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer'
                }}
                title="Tap the map to unlock and edit"
                aria-label="Unlock location editing"
              >
                Location locked — tap the map to edit
              </button>
            )}
          </div>

          {/* AREA AND LINK AFTER THE PIN (2026-08-30). Both are answered BY the
              pin — the link auto-fills from it and the area is suggested from
              it — so asking for them first made an admin guess and then correct
              themselves. Pin first, then confirm what the pin implies. */}
          <div className="form-row area-link-row" style={{ alignItems: 'flex-end', gap: 12, marginTop: 18 }}>
            {/* Area (dropdown react-select) */}
            <div className="form-group" style={{ flex: 1, minWidth: 240 }}>
              <label>Area</label>
              <Select
                inputId="area-select"
                options={areaOptions}
                value={areaValue}
                placeholder="Select area"
                onChange={(opt) =>
                  setNewProperty((prev) => ({
                    ...prev,
                    area: opt ? opt.value : '',
                  }))
                }
                styles={{
                  container: (base) => ({ ...base, zIndex: 12 }),
                  // react-select v5 lays this out as a GRID and stacks its
                  // children in one cell — an extra child (our chip) therefore
                  // opened a second row and made the control 64px tall
                  // (2026-08-30). Flex puts the value and the chip side by
                  // side on one line, which is the whole point.
                  valueContainer: (base) => ({
                    ...base,
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    paddingRight: 4,
                    gap: 8,
                    overflow: 'hidden',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    position: 'static',
                    transform: 'none',
                    maxWidth: 'none',
                    margin: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }),
                  placeholder: (base) => ({
                    ...base, position: 'static', transform: 'none', margin: 0,
                  }),
                  input: (base) => ({ ...base, margin: 0, padding: 0, flex: '0 1 auto' }),
                }}
                formatOptionLabel={formatAreaOption}
                components={{ ValueContainer: AreaValueContainer }}
                isLoading={suggesting}
                areaStatus={
                  suggesting ? (
                    <span className="area-chip area-chip--busy">Checking the pin…</span>
                  ) : !suggestedArea ? null : suggestionMatches ? (
                    <span
                      className="area-chip area-chip--ok"
                      title={suggestion.confidence === 'inside'
                        ? `The pin is inside ${suggestedArea}`
                        : 'Nearest covered area to the pin'}
                    >
                      ✓ Matches pin
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={`area-chip area-chip--${suggestion.confidence}`}
                      title={suggestion.confidence === 'far'
                        ? 'The pin is over 1 km from every area we cover'
                        : `The pin is ${suggestion.confidence === 'inside'
                            ? 'inside'
                            : `${Math.round(suggestion.metres)} m from`} ${suggestedArea}`}
                      /* Keep the menu shut — this is a shortcut, not a value pick. */
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewProperty((prev) => ({ ...prev, area: suggestedArea }));
                      }}
                    >
                      Use {suggestedArea}
                    </button>
                  )
                }
          />
            </div>

            {/* Google Maps Link (read-only, auto). flex, NOT width:100% — the
                latter claimed the whole row and pushed Area onto a line of its
                own, on a row with room to spare (2026-08-30). */}
            <div className="form-group" style={{ flex: 2, minWidth: 280 }}>
              <label>Google Maps Link</label>
              <div style={{ position: 'relative', width: '100%' }}>
                <input
                  type="text"
                  value={newProperty.google_maps_link || ''}
                  readOnly
                  placeholder="Will auto-fill after Search/Map"
                  aria-label="Google Maps Link"
                  title={
                    newProperty.google_maps_link
                      ? 'Auto-filled from selection'
                      : 'No link yet'
                  }
                  style={{
                    width: '100%',
                    background: 'rgb(245,245,245)',
                    cursor: 'not-allowed',
                    paddingRight: 44,
                    height: 36,
                    lineHeight: '36px',
                  }}
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={!newProperty?.google_maps_link}
                  aria-label={
                    newProperty?.google_maps_link
                      ? 'Copy Google Maps link'
                      : 'No link to copy yet'
                  }
                  title={
                    newProperty?.google_maps_link
                      ? 'Copy link'
                      : 'No link to copy yet'
                  }
                  style={{
                    position: 'absolute',
                    right: 6,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgb(245, 245, 245)',
                    height: 28,
                    width: 32,
                    color: '#3c3c3cff',
                    cursor: newProperty?.google_maps_link ? 'pointer' : 'not-allowed',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    border: 'none',
                    borderRadius: 6,
                  }}
                >
                  <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </APIProvider>
  );
}

function PlacesSearch({ onPick, initialText }) {
  const placesLib = useMapsLibrary('places'); // ensures the Places library is loaded
  const hostRef = useRef(null);
  const elementRef = useRef(null);

  useEffect(() => {
    if (!placesLib || !hostRef.current || elementRef.current) return;

    const host = hostRef.current;
    const el = new window.google.maps.places.PlaceAutocompleteElement();

    // 🔒 Limit to UAE
    try { el.includedRegionCodes = ['ae']; } catch { }

    // 🎨 Basic look & feel (host-level styling)
    el.placeholder = 'Search building or address';
    el.style.width = '100%';
    el.style.height = '40px';
    el.style.border = '1px solid hsl(0, 0%, 80%)';
    el.style.borderRadius = '5px';
    el.style.background = '#fff';

    const onSelect = async (evt) => {
      const pp = evt?.placePrediction;
      if (!pp) return;

      const place = pp.toPlace();
      await place.fetchFields({
        fields: ['id', 'location', 'displayName', 'googleMapsLinks']
      });

      const loc = place.location;
      if (!loc) return;

      const mapsUri = place.googleMapsLinks.placeURI || null;

      onPick({
        lat: loc.lat(),
        lng: loc.lng(),
        placeId: place.id || null,
        placeDesc: place.displayName || '',
        mapsUri
      });
    };

    el.addEventListener('gmp-select', onSelect);
    host.appendChild(el);
    elementRef.current = el;

    return () => {
      try {
        el.removeEventListener('gmp-select', onSelect);
        if (host.contains(el)) host.removeChild(el);
      } catch { }
      elementRef.current = null;
    };
  }, [placesLib, onPick, initialText]);

  // If initialText changes later, keep the visible value in sync
  useEffect(() => {
    if (elementRef.current && typeof initialText === 'string') {
      try { elementRef.current.value = initialText; } catch { }
    }
  }, [initialText]);

  return <div ref={hostRef} />;
}

function MapClick({ onPick, enabled = true, onRequestEnable }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const listener = map.addListener('click', (e) => {
      if (!enabled) {
        // When locked, a tap on the map just unlocks editing
        onRequestEnable?.();
        return;
      }
      const lat = e.latLng?.lat?.();
      const lng = e.latLng?.lng?.();
      if (typeof lat === 'number' && typeof lng === 'number') onPick({ lat, lng });
    });
    return () => listener?.remove?.();
  }, [map, onPick, enabled, onRequestEnable]);

  return null;
}

function PanTo({ position, zoom = 16 }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !position) return;
    map.panTo(position);
    if (typeof zoom === 'number') map.setZoom(zoom);
  }, [map, position, zoom]);
  return null;
}

/**
 * The Area field's own value row, with the pin's verdict sitting inside it.
 *
 * 2026-08-30: "cant you put the matches the pin inside the field itself not in
 * a new row… it would look smoother" — and it does. The chip rides in the
 * control next to the selected value instead of pushing a whole row into the
 * form. `areaStatus` is passed through Select's selectProps.
 */
function AreaValueContainer({ children, ...props }) {
  const status = props.selectProps.areaStatus;
  return (
    <rsComponents.ValueContainer {...props}>
      {children}
      {status}
    </rsComponents.ValueContainer>
  );
}

/**
 * The covered areas, drawn under the pin.
 *
 * @vis.gl/react-google-maps ships no Polygon, so the shapes are created against
 * the map instance and torn down with it. clickable:false is deliberate — this
 * map's whole job is letting somebody tap a location, and a clickable overlay
 * would swallow those taps.
 */
function AreaZones({ zones }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !window.google?.maps || !zones?.length) return undefined;

    const drawn = [];
    zones.forEach((z) => {
      const color = colorForArea(z.area) || '#94a3b8';
      const style = {
        map,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 1.5,
        fillColor: color,
        fillOpacity: 0.10,
        clickable: false,
        zIndex: 1,
      };
      const rings = z.boundary?.length ? z.boundary : (z.polygon?.length >= 3 ? [z.polygon] : null);
      if (rings) {
        drawn.push(new window.google.maps.Polygon({ ...style, paths: rings }));
      } else if (z.lat != null && z.lng != null && z.radius) {
        drawn.push(new window.google.maps.Circle({
          ...style, center: { lat: z.lat, lng: z.lng }, radius: z.radius,
        }));
      }
    });

    return () => drawn.forEach((sh) => sh.setMap(null));
  }, [map, zones]);

  return null;
}
